import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import cn from 'classnames'
import { useTranslation } from 'react-i18next'

import { CameraIcon, ChatBubbleInstaIcon, HeartIcon, MoreHorizIcon, SendIcon } from 'images'
import CarouselMedia from './CarouselMedia'

// Instagram Reels ad card (Figma node `31504:26324`) — full-bleed video/image
// background with overlaid caption/CTA/interaction icons; no separate link bar
// or action row (structurally different from FeedAdCard).
//
// When `ad.carouselCards` is present, the full-bleed background swaps to
// CarouselMedia's chevron-advanced single-card strip with a `reels-carousel`
// modifier class.
const ReelsAdCard = ({ ad }) => {
  const bodyRef = useRef(null)
  const [isLongText, setIsLongText] = useState(false)
  const [showMore, setShowMore] = useState(false)
  const { t } = useTranslation()

  useEffect(() => {
    if (bodyRef.current) setIsLongText(bodyRef.current.scrollHeight > bodyRef.current.clientHeight)
  }, [])

  const { pageName, profilePictureUrl, body, callToAction, media, carouselCards } = ad
  const isCarousel = Array.isArray(carouselCards) && carouselCards.length > 0
  const isVideo = Boolean(media?.type?.includes('video'))

  return (
    <div className={cn('reel', { 'reels-carousel': isCarousel })}>
      {isCarousel ? (
        <CarouselMedia platform="reels" cards={carouselCards} />
      ) : isVideo ? (
        <video className="reel__video" src={media.url} autoPlay loop muted playsInline />
      ) : (
        <div style={{ backgroundImage: `url(${media?.url})` }} className="reel__background" />
      )}

      <div className="reel__main-title">
        <h3>
          <strong>Reels</strong>
        </h3>
        <div>
          <CameraIcon width={25} />
        </div>
      </div>

      <div className="reel__info">
        <div className="d-flex px-3 py-2">
          <div className="d-flex flex-column gap-2 justify-content-end" style={{ width: '85%' }}>
            <div className="d-flex align-items-center w-100">
              <div>
                {profilePictureUrl ? (
                  <img alt={pageName} className="reel__profile_picture" src={profilePictureUrl} />
                ) : (
                  <div className="reel__icon" />
                )}
              </div>
              {pageName ? (
                <p className="reel__title">
                  {pageName}
                  <span className="subtitle">Sponsored</span>
                </p>
              ) : (
                <div className="fb-error">{t('Recommendations.summary.fbPageInfoError')}</div>
              )}
            </div>

            <p className="reel__caption">
              <span ref={bodyRef} className={showMore ? 'text' : 'text_clipped'}>
                {body}
              </span>
              {isLongText && (
                <span className="more" onClick={() => setShowMore((prev) => !prev)}>
                  {showMore ? 'less' : 'more'}
                </span>
              )}
            </p>
          </div>
          <div className="reel__interactions" style={{ width: '15%' }}>
            <div className="d-flex flex-column gap-4">
              <span>
                <HeartIcon />
              </span>
              <span>
                <ChatBubbleInstaIcon />
              </span>
              <span>
                <SendIcon />
              </span>
              <span>
                <MoreHorizIcon />
              </span>
            </div>
          </div>
        </div>
        <div className="p-3 pt-0">
          <button className="reel__order-now">
            <div>{callToAction}</div>
          </button>
        </div>
      </div>
    </div>
  )
}

ReelsAdCard.propTypes = {
  ad: PropTypes.shape({
    pageName: PropTypes.string,
    profilePictureUrl: PropTypes.string,
    body: PropTypes.string,
    callToAction: PropTypes.string,
    media: PropTypes.object,
    carouselCards: PropTypes.array,
  }).isRequired,
}

export default ReelsAdCard
